import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { EnhancedStore, UnknownAction } from '@reduxjs/toolkit';
import { store } from '../redux/store';
import {
  ConnectionStatus,
  resetConnection,
  setConnected,
  setConnecting,
  setConnectionFailed,
  setConnectionStatus
} from '@/redux/slices/signalRSlice';
import { datadogRum, DataDogSeverityLevel } from '@/lib/DatadogService';
import creditPlanEndpoints from '@/redux/api/client/creditPlan';
import { upsertPlan } from '@/redux/slices/creditPlanSlice';
import { CreditPlanStatus } from '@/@types/client-api/models/CreditPlanStatus';
import { deconstructCreditPlanLicenseIdentifier } from '@/components/ui/CreditPlan/util/CreditPlanHelpers';

enum SignalRMethod {
  ReceiveStatus = 'ReceiveStatus',
  ReceiveCompleted = 'ReceiveCompleted',
  ReceiveFailed = 'ReceiveFailed'
}

const hubConnection = new HubConnectionBuilder()
  .withUrl(`${import.meta.env.VITE_CONTENT_DELIVERY_API}/api/client/signalr`, {
    skipNegotiation: false,
    // Don't specify transport type, allow it to default to best option available for client
    accessTokenFactory: () => store.getState().auth.token ?? ''
  })
  .withAutomaticReconnect()
  .configureLogging(LogLevel.Information)
  .build();

// central lifecycle promise to ensure start/stop calls are sequential and not overlapping
let lifecycle = Promise.resolve();

hubConnection.onclose(err => {
  console.info('SignalR connection closed');
  if (err) {
    console.error('SignalR connection closed with error: ', err);
    datadogRum.addError(err, {
      severityLevel: DataDogSeverityLevel.Warning,
      component: 'SignalR',
      context: 'onclose'
    });
    store.dispatch(setConnectionFailed(err.toString()));
  } else {
    store.dispatch(resetConnection());
  }
});

hubConnection.onreconnecting(err => {
  console.error('SignalR is attempting to reconnect, error', {
    error: err
  });
  datadogRum.addError(err, {
    severityLevel: DataDogSeverityLevel.Warning,
    component: 'SignalR',
    context: 'onreconnecting'
  });
  store.dispatch(setConnecting());
});

// Register message handlers
const registerMessageHandlers = (store: EnhancedStore) => {
  Object.values(SignalRMethod).forEach(method => hubConnection.off(method));
  // Register handlers per SignalR method name
  hubConnection.on(
    SignalRMethod.ReceiveStatus,
    (message: { ProcessId: string; LicenseIdentifier: string; Message: string }) => {
      console.info('SignalR received Status message', {
        message,
        timestamp: new Date().toISOString()
      });

      const { jurisdiction } = deconstructCreditPlanLicenseIdentifier(message.LicenseIdentifier);
      store.dispatch(
        upsertPlan({
          jurisdiction: jurisdiction,
          processId: message.ProcessId,
          licenseIdentifier: message.LicenseIdentifier,
          plan: {
            jurisdiction: jurisdiction,
            processId: message.ProcessId,
            licenseIdentifier: message.LicenseIdentifier,
            status: message.Message as CreditPlanStatus
          }
        })
      );
    }
  );
  hubConnection.on(SignalRMethod.ReceiveCompleted, (message: { ProcessId: string }) => {
    console.info('SignalR received Completed message', {
      message,
      timestamp: new Date().toISOString()
    });
    console.info('Dispatching getCreditPlan for processId ', message.ProcessId);
    // Refetch credit plan by id upon completion status update
    store.dispatch(
      creditPlanEndpoints.endpoints.getCreditPlan.initiate(
        message.ProcessId
      ) as unknown as UnknownAction
    );
  });
  hubConnection.on(SignalRMethod.ReceiveFailed, (message: string) => {
    console.info('SignalR received Failed message', {
      message,
      timestamp: new Date().toISOString()
    });
    // TODO: handle failure state.
  });
  console.info('SignalR message handlers registered');
};

function start() {
  console.info('Starting SignalR connection...');
  return hubConnection
    .start()
    .then(() => {
      console.info(`SignalR connector started, connectionId ${hubConnection.connectionId}`);
      store.dispatch(setConnected());
    })
    .catch(err => {
      console.error('SignalR connection start failed, error: ', err);
      datadogRum.addError(err, {
        severityLevel: DataDogSeverityLevel.Error,
        component: 'SignalR',
        context: 'start'
      });
      store.dispatch(setConnectionFailed(err.toString()));
      // TODO: re-try start, a fixed number of attempts.
    });
}

function stop() {
  console.info('Stopping SignalR connection...');
  return hubConnection
    .stop()
    .then(() => {
      console.info(`SignalR connector stopped, connectionId ${hubConnection.connectionId}`);
      store.dispatch(setConnectionStatus(ConnectionStatus.Uninitialized));
    })
    .catch(err => {
      console.error('SignalR connection stop failed, error: ', err);
      datadogRum.addError(err, {
        severityLevel: DataDogSeverityLevel.Error,
        component: 'SignalR',
        context: 'stop'
      });
    });
}

export function startSignalR() {
  // per microsoft docs:
  // A best practice is to call the start method on the HubConnection after on.
  // Doing so ensures the handlers are registered before any messages are received.
  lifecycle = lifecycle.then(() => {
    registerMessageHandlers(store);
    store.dispatch(setConnecting());
    return start();
  });
  return lifecycle;
}

export function stopSignalR() {
  lifecycle = lifecycle.then(() => {
    store.dispatch(resetConnection());
    return stop();
  });
  return lifecycle;
}

export default hubConnection;
